import { useState, useEffect, useCallback } from 'react';
import { apiFetch, getToken } from '../dppApi';

// The collector's open inquiries (My Collection side panel). The conversation with
// the boutique lives in one timeline (chat order, oldest→newest); messaging and
// scheduling are separate. The appointment time only surfaces at the top once BOTH
// sides have agreed (confirmed) — while a time is being negotiated it stays a
// proposal in the timeline.
const meetingLabel = (t) => (t === 'virtual' ? 'Virtual call' : 'In-store visit');
const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};
// Same timeline verbs as the brand card (see BrandInquiries).
const EVENT_VERB = { asked: 'requested', proposed: 'proposed', countered: 'responded with', confirmed: 'confirmed' };
const HISTORY_HEAD = 1;   // collapsed, only the most recent message shows; the rest fold behind the toggle

const UserInquiries = () => {
    const token = getToken('user');
    const [inquiries, setInquiries] = useState([]);
    const [counterFor, setCounterFor] = useState(null);   // { id, when, action } — time picker (counter | reschedule)
    const [msgs, setMsgs] = useState({});                 // per-inquiry composed message
    const [times, setTimes] = useState({});               // per-inquiry proposed new time (bundled with Send)
    const [timeOpen, setTimeOpen] = useState({});         // per-inquiry: the "Propose New Time" field is showing
    const [historyOpen, setHistoryOpen] = useState({});   // per-inquiry: full history expanded

    const load = useCallback(() => {
        apiFetch('/api/inquiry', { token }).then(setInquiries).catch(() => {});
    }, [token]);

    // Non-critical panel — if it fails, just stay hidden (see UserCollection).
    // Poll + refetch on focus so the merchant's reply/proposal appears without a
    // manual refresh (the negotiation is asynchronous).
    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
    }, [load]);

    const patch = async (id, body, opts = {}) => {
        try {
            await apiFetch(`/api/inquiry/${id}`, { method: 'PATCH', token, body });
            if (!opts.keepForm) setCounterFor(null);   // a message send leaves an open time picker alone
            load();
        } catch { /* leave the panel as-is on failure */ }
    };

    // Grow the message box to fit its content — one line by default, taller as needed.
    const autoGrow = (el) => { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; };

    const msgText = (r) => (msgs[r.inquiry_id] ?? '');
    const onMsgChange = (id, v) => setMsgs(m => ({ ...m, [id]: v }));
    // The new time bundled into the composer (only on a confirmed appt, field open + value).
    const composerTime = (r) => ((r.status === 'confirmed' && timeOpen[r.inquiry_id]) ? (times[r.inquiry_id] || '').trim() : '');
    const composerCanSend = (r) => !!(msgText(r).trim() || composerTime(r));
    // One combined Send: with a new time it reschedules (re-opens agreement) and carries
    // the fresh note; otherwise it's a plain message.
    const sendComposer = (r) => {
        const text = msgText(r).trim();
        const when = composerTime(r);
        if (!text && !when) return;
        setMsgs(m => { const n = { ...m }; delete n[r.inquiry_id]; return n; });
        setTimes(m => { const n = { ...m }; delete n[r.inquiry_id]; return n; });
        setTimeOpen(m => { const n = { ...m }; delete n[r.inquiry_id]; return n; });
        if (when) patch(r.inquiry_id, { action: 'reschedule', appointment_time: when, message: text });
        else patch(r.inquiry_id, { action: 'message', message: text }, { keepForm: true });
    };

    if (!inquiries.length) return null;

    // Time picker — counter the merchant's proposal, or reschedule a confirmed
    // appointment (which re-opens the agreement). No message rides along.
    const timeForm = (r) => {
        const isResched = counterFor?.action === 'reschedule';
        return (
            <div className="inquiry-user-counter">
                <input className="dpp-input" type="datetime-local"
                    value={counterFor.when}
                    onChange={e => setCounterFor(c => ({ ...c, when: e.target.value }))} />
                <div className="inquiry-user-actions">
                    <button className="catalogue-btn-primary" disabled={!counterFor.when}
                        onClick={() => patch(r.inquiry_id, { action: counterFor.action, appointment_time: counterFor.when })}>
                        {isResched ? 'Send new time' : 'Send proposal'}
                    </button>
                    <button className="catalogue-btn-secondary" onClick={() => setCounterFor(null)}>Cancel</button>
                </div>
            </div>
        );
    };

    return (
        <div className="dpp-card insights-card assistant-card maintenance-user-panel">
            <div className="insights-card-title">Your inquiries</div>
            <div className="maintenance-user-list">
                {inquiries.map(r => {
                    const picking = counterFor?.id === r.inquiry_id;
                    const upcoming = r.appointment_time && new Date(r.appointment_time) > new Date();
                    const events = r.events || [];   // chat order: oldest → newest
                    const isExp = !!historyOpen[r.inquiry_id];
                    const hidden = Math.max(0, events.length - HISTORY_HEAD);
                    const visible = (isExp || hidden === 0) ? events : events.slice(-HISTORY_HEAD);
                    return (
                        <div key={r.inquiry_id} className="maintenance-user-item">
                            {r.image
                                ? <img className="maintenance-user-thumb" src={r.image} alt={r.product} />
                                : <div className="maintenance-user-thumb maintenance-user-thumb--empty" />}
                            <div className="maintenance-user-body">
                                <div className="maintenance-user-name inquiry-user-name">{r.product || 'Inquiry'}</div>
                                <div className="maintenance-user-issue">{meetingLabel(r.meeting_type)}</div>
                                {/* Agreed time surfaces only once confirmed. */}
                                {r.status === 'confirmed' && r.appointment_time && (
                                    <div className="inquiry-user-appt-top">{fmt(r.appointment_time)}</div>
                                )}

                                <div className="inquiry-thread">
                                    {/* One continuous timeline (chat order); toggle reveals earlier history. */}
                                    {events.length > 0 && (
                                        <div className="inquiry-timeline">
                                            {hidden > 0 && (
                                                <button type="button" className="inquiry-tl-toggle"
                                                    onClick={() => setHistoryOpen(m => ({ ...m, [r.inquiry_id]: !isExp }))}>
                                                    {isExp ? 'Hide conversation' : `Show conversation (${hidden})`}
                                                </button>
                                            )}
                                            {visible.map((e, i) => {
                                                const you = e.actor === 'customer';
                                                const who = you ? 'You' : (r.merchant || 'Boutique');
                                                const dot = you ? 'grey' : 'black';   // you = grey, other party = black
                                                const verb = EVENT_VERB[e.kind];
                                                return (
                                                    <div className="inquiry-ev" key={`${e.created_at}-${e.kind}-${i}`}>
                                                        <span className={`inquiry-ev-dot inquiry-ev-dot--${dot}`} />
                                                        <div className="inquiry-ev-body">
                                                            <div className="inquiry-ev-top">
                                                                <span className="inquiry-ev-who">{who}</span>
                                                                <span className="inquiry-ev-time">{fmt(e.created_at)}</span>
                                                            </div>
                                                            {e.body && <div className="inquiry-ev-text">“{e.body}”</div>}
                                                            {e.kind === 'completed' && <div className="inquiry-ev-note">Marked completed</div>}
                                                            {e.kind === 'cancelled' && <div className="inquiry-ev-note">Cancelled</div>}
                                                            {e.appointment_time && verb && (
                                                                <span className="inquiry-ev-appt">{verb} · {fmt(e.appointment_time)}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Always-on message box, docked below the timeline — stays
                                        visible even while the time picker is open, so a half-typed
                                        message is never swallowed. */}
                                    <div className="inquiry-reply-dock">
                                        <textarea className="dpp-input inquiry-reply-text" rows={1}
                                            ref={el => el && autoGrow(el)}
                                            placeholder="Message the boutique…"
                                            value={msgText(r)}
                                            onChange={e => { onMsgChange(r.inquiry_id, e.target.value); autoGrow(e.target); }} />
                                        {/* Reschedule, bundled into the composer: propose a new time and send it
                                            with the message in one Send. Only on a confirmed, upcoming appt. */}
                                        {r.status === 'confirmed' && upcoming && timeOpen[r.inquiry_id] && (
                                            <input className="dpp-input inquiry-reply-time" type="datetime-local"
                                                value={times[r.inquiry_id] || ''}
                                                onChange={e => setTimes(m => ({ ...m, [r.inquiry_id]: e.target.value }))} />
                                        )}
                                        <div className="inquiry-reply-send">
                                            {r.status === 'confirmed' && upcoming && (
                                                <button type="button" className="catalogue-optional-toggle inquiry-proposetime"
                                                    onClick={() => setTimeOpen(m => ({ ...m, [r.inquiry_id]: !m[r.inquiry_id] }))}>
                                                    {timeOpen[r.inquiry_id] ? '– Propose New Time' : '+ Propose New Time'}
                                                </button>
                                            )}
                                            <button className="catalogue-btn-primary" disabled={!composerCanSend(r)}
                                                onClick={() => sendComposer(r)}>Send</button>
                                        </div>
                                    </div>

                                    {/* Scheduling — separate from messaging. */}
                                    {picking ? timeForm(r) : (
                                        <>
                                            {r.status === 'pending' && (
                                                <div className="inquiry-user-actions">
                                                    <span className="inquiry-user-status">Awaiting a proposed time</span>
                                                    <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                                                </div>
                                            )}
                                            {r.status === 'proposed' && (
                                                <div className="inquiry-user-actions">
                                                    <button className="catalogue-btn-primary" onClick={() => patch(r.inquiry_id, { action: 'accept' })}>Accept {fmt(r.appointment_time)}</button>
                                                    <button className="catalogue-btn-secondary" onClick={() => setCounterFor({ id: r.inquiry_id, when: '', action: 'counter' })}>Suggest another time</button>
                                                    <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                                                </div>
                                            )}
                                            {r.status === 'countered' && (
                                                <div className="inquiry-user-actions">
                                                    <span className="inquiry-user-status">Awaiting the merchant.</span>
                                                    <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                                                </div>
                                            )}
                                            {r.status === 'flagged' && (
                                                <div className="inquiry-user-actions">
                                                    <span className="inquiry-user-status">The merchant will accommodate your time.</span>
                                                    <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                                                </div>
                                            )}
                                            {r.status === 'confirmed' && upcoming && (
                                                <div className="inquiry-user-actions">
                                                    <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default UserInquiries;
